import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import ScrollToTop from '@components/ScrollToTop'
import Layout from '@components/Layout'
import Home from '@pages/Home'
import { useAuth } from './context/AuthContext'

// Every route below Home is code-split — the homepage's own JS bundle
// should only contain what the homepage needs, not the entire app
// (dashboard, item editors, admin-ish pages, etc). Each lazy import
// becomes its own chunk, fetched only when that route is visited.
const About = lazy(() => import('@pages/About'))
const Terms = lazy(() => import('@pages/Terms'))
const Login = lazy(() => import('@pages/Login'))
const SignUp = lazy(() => import('@pages/SignUp'))
const PhoneVerification = lazy(() => import('@pages/PhoneVerification'))
const CollectionType = lazy(() => import('@pages/CollectionType'))
const ProfileSetup = lazy(() => import('@pages/ProfileSetup'))
const SignupComplete = lazy(() => import('@pages/SignupComplete'))
const ForgotPassword = lazy(() => import('@pages/ForgotPassword'))
const MyCollections = lazy(() => import('@pages/MyCollections'))
const CollectionDetails = lazy(() => import('@pages/CollectionDetails'))
const AddEditItem = lazy(() => import('@pages/AddEditItem'))
const ItemDetails = lazy(() => import('@pages/ItemDetails'))
const Dashboard = lazy(() => import('@pages/Dashboard'))
const Showcase = lazy(() => import('@pages/Showcase'))
const CreateShowcase = lazy(() => import('@pages/CreateShowcase'))
const Profile = lazy(() => import('@pages/Profile'))
const ProfileSettings = lazy(() => import('@pages/ProfileSettings'))
const Help = lazy(() => import('@pages/Help'))
const Privacy = lazy(() => import('@pages/Privacy'))
const Collectors = lazy(() => import('@pages/Collectors'))
const Challenges = lazy(() => import('@pages/Challenges'))
const ChallengeDetails = lazy(() => import('@pages/ChallengeDetails'))
const ChallengeItemDetails = lazy(() => import('@pages/ChallengeItemDetails'))
const ChallengeCoinsLanding = lazy(() => import('@pages/ChallengeCoinsLanding'))
const ChallengeManuscriptsLanding = lazy(() => import('@pages/ChallengeManuscriptsLanding'))
const Blog = lazy(() => import('@pages/Blog'))
const BlogArticle = lazy(() => import('@pages/BlogArticle'))
const Careers = lazy(() => import('@pages/Careers'))
const Press = lazy(() => import('@pages/Press'))
const Contact = lazy(() => import('@pages/Contact'))
const HelpPublic = lazy(() => import('@pages/HelpPublic'))
const CookiePolicy = lazy(() => import('@pages/CookiePolicy'))
const Accessibility = lazy(() => import('@pages/Accessibility'))
const PostDetail = lazy(() => import('@pages/PostDetail'))
const PublicProfile = lazy(() => import('@pages/PublicProfile'))

const RouteLoader = () => (
  <div className="min-h-screen bg-museum-ivory flex items-center justify-center">
    <div className="w-6 h-6 border border-museum-antique-gold/40 border-t-museum-antique-gold rounded-full animate-spin" />
  </div>
)

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-deep-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-charcoal/60">Loading...</p>
        </div>
      </div>
    )
  }

  return user ? children : <Navigate to="/login" replace />
}

// Redirect logged-in users to the right place based on profile completion
const AuthRedirect = ({ fallback = null }) => {
  const { user, backendUser, loading, backendLoading } = useAuth()

  if (loading || (user && backendLoading)) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-deep-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-charcoal/60">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) return fallback

  if (backendUser && backendUser.profileComplete === false) {
    return <Navigate to="/signup/collection-type" replace />
  }

  return <Navigate to="/dashboard" replace />
}

function App() {
  const { user, loading } = useAuth()

  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<RouteLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={loading ? null : user ? <Navigate to="/dashboard" replace /> : <Layout />}>
            <Route index element={<Home />} />
          </Route>

          {/* Public pages with layout */}
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />

          {/* Public post detail — shareable, works logged in/out */}
          <Route path="/posts/:postId" element={<PostDetail />} />

          {/* Public profile pages — accessible without login */}
          <Route path="/u/:username" element={<PublicProfile />} />

          {/* Profile by ID — also public, adaptive layout */}
          <Route path="/profile/:userId?" element={<Profile />} />
          {/* Public challenge landing pages */}
          <Route path="/challenge/ancient-coins" element={<ChallengeCoinsLanding />} />
          <Route path="/challenge/rare-manuscripts" element={<ChallengeManuscriptsLanding />} />

          {/* Public company & support pages */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/press" element={<Press />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/help-center" element={<HelpPublic />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/accessibility" element={<Accessibility />} />

          {/* Auth routes */}
          <Route path="/login" element={<AuthRedirect fallback={<Login />} />} />
          <Route path="/signup" element={<AuthRedirect fallback={<SignUp />} />} />
          <Route path="/signup/verify-phone" element={<PhoneVerification />} />
          <Route path="/signup/collection-type" element={<CollectionType />} />
          <Route path="/signup/profile" element={<ProfileSetup />} />
          <Route path="/signup/complete" element={<SignupComplete />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Protected routes */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/collections" element={<MyCollections />} />
          <Route path="/collections/:id" element={<CollectionDetails />} />
          <Route path="/collections/:collectionId/add-item" element={<ProtectedRoute><AddEditItem /></ProtectedRoute>} />
          <Route path="/collections/:collectionId/items/:itemId/edit" element={<ProtectedRoute><AddEditItem /></ProtectedRoute>} />
          <Route path="/collections/:collectionId/items/:itemId" element={<ItemDetails />} />
          <Route path="/items/:itemId" element={<ItemDetails />} />
          <Route path="/showcase" element={<ProtectedRoute><Showcase /></ProtectedRoute>} />
          <Route path="/showcase/create" element={<ProtectedRoute><CreateShowcase /></ProtectedRoute>} />
          <Route path="/profile/settings" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
          <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
          <Route path="/collectors" element={<ProtectedRoute><Collectors /></ProtectedRoute>} />
          <Route path="/challenges/:id" element={<ProtectedRoute><ChallengeDetails /></ProtectedRoute>} />
          <Route path="/challenges/:challengeId/items/:itemId" element={<ProtectedRoute><ChallengeItemDetails /></ProtectedRoute>} />
          <Route path="/challenges/:challengeId/items/:itemId/edit" element={<ProtectedRoute><AddEditItem /></ProtectedRoute>} />
          <Route path="/challenges/:challengeId/add-item" element={<ProtectedRoute><AddEditItem /></ProtectedRoute>} />
        </Routes>
      </Suspense>
    </Router>
  )
}

export default App
